import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  Card,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
// Remove direct import, will be used in route
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  getAllDailyPunchesAction,
  clearGetAllDailyPunchesAction,
  getDailyPunchByIdAction,
  clearGetDailyPunchByIdAction,
  getAllUserAction,
} from "../../../store/actions";

// ── helpers ────────────────────────────────────────────────────────────────────
const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${date}  ${time}`;
  } catch {
    return iso;
  }
};

const getDistance = (record) => {
  const { totalDistanceKm, odometerStart, odometerEnd } = record;
  if (totalDistanceKm != null) return `${Number(totalDistanceKm)} km`;
  if (odometerStart != null && odometerEnd != null)
    return `${Math.max(0, Number(odometerEnd) - Number(odometerStart))} km`;
  return "-";
};

// Get date part (YYY-MM-DD) from ISO string for comparison
const getDateOnly = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  } catch {
    return null;
  }
};

// Check if a record is from a previous day (not today)
const isPreviousDayRecord = (record) => {
  if (!record?.startPunchTime) return false;
  const recordDate = getDateOnly(record.startPunchTime);
  const todayDate = getDateOnly(new Date().toISOString());
  return recordDate && todayDate && recordDate < todayDate;
};

const DESKTOP_COLS = "1fr 1fr 1fr 1fr 1fr 1fr 0.2fr";
const HEADERS = ["Start Time",  "Start Reading", "End Time",  "End Reading", "Total Distance", "Actions"];


// ── Main List Component ────────────────────────────────────────────────────────
const TravelPunchingLogList = () => {
  console.log('TravelPunchingLogList rendered, current path:', window.location.pathname);
      // State for viewing travel log details
      // Remove viewRecord state
    // Track if Get Activity was clicked for a non-logged-in user
  const [showOtherUserData, setShowOtherUserData] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState(() => {
    return searchParams.get("userId") || "";
  });
  // Month filter state: default to current month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return searchParams.get("month") || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  
  const loggedInUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails"))?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const userRole = useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem("userDetails"))?.role ?? null;
      console.log("User Role from localStorage:", role); // Debug log
      return role;
    } catch {
      return null;
    }
  }, []);

  // Check if user is admin, AE, or ZM (case-insensitive)
  const isRestrictedRole = useMemo(() => {
    if (!userRole) return false;
    const normalizedRole = userRole.toLowerCase().trim();
    const isRestricted = ["admin", "ae", "zm"].includes(normalizedRole);
    console.log("Normalized role:", normalizedRole, "Is Restricted:", isRestricted); // Debug log
    return isRestricted;
  }, [userRole]);

  // Clear filters on page refresh
  useEffect(() => {
    if (isRestrictedRole) {
      setSelectedUserId("");
      setSelectedMonth(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
      setSearchParams({});
    }
  }, [isRestrictedRole]);

  const punchingLogPath = location.pathname.startsWith("/SOorMDO")
    ? "/SOorMDO/travelPunchingLog"
    : "/masterData/travelPunchingLog";

  const { loading, successData, error, errorInfo } = useSelector(
    (state) => state.getAllDailyPunchesReducer || {}
  );

  const { successData: byIdSuccess } = useSelector((state) => state.getDailyPunchByIdReducer || {});

  const { successData: allUsersData, loading: usersLoading } = useSelector(
    (state) => {
      console.log("📊 Redux state.getAllUserReducer:", state.getAllUserReducer);
      return state.getAllUserReducer || {};
    }
  );

  // Extract records first (before using in allUsers)
  const records = useMemo(() => {
    if (!successData) return [];
    let allRecords = [];
    if (Array.isArray(successData?.data?.travels)) allRecords = successData.data.travels;
    else if (Array.isArray(successData?.data)) allRecords = successData.data;
    else if (Array.isArray(successData)) allRecords = successData;

    // If showing other user data, show as fetched
    if (showOtherUserData && selectedUserId && selectedUserId !== loggedInUserId) {
      return allRecords.filter(r => {
        if (r.userId !== selectedUserId) return false;
        if (!r.travelDate) return false;
        const recordMonth = r.travelDate.slice(0, 7);
        return recordMonth === selectedMonth;
      });
    }
    // If logged-in user is selected (or no user selected), always show their activities
    if (!selectedUserId || selectedUserId === loggedInUserId) {
      return allRecords.filter(r => {
        if (r.userId !== loggedInUserId) return false;
        if (!r.travelDate) return false;
        const recordMonth = r.travelDate.slice(0, 7);
        return recordMonth === selectedMonth;
      });
    }
    // Otherwise, don't show any data (until Get Activity is clicked)
    return [];
  }, [successData, showOtherUserData, selectedUserId, loggedInUserId, selectedMonth]);

  const allUsers = useMemo(() => {
    console.log("🔍 allUsers useMemo called");
    console.log("   allUsersData:", allUsersData);
    
    if (!allUsersData) {
      console.log("❌ allUsersData is null or undefined");
      return [];
    }
    
    let users = [];
    
    // API returns data.users, not data.data
    if (allUsersData?.data?.users && Array.isArray(allUsersData.data.users)) {
      users = allUsersData.data.users;
      console.log("✅ Using allUsersData.data.users");
    } else if (allUsersData?.users && Array.isArray(allUsersData.users)) {
      users = allUsersData.users;
      console.log("✅ Using allUsersData.users");
    } else if (Array.isArray(allUsersData?.data)) {
      users = allUsersData.data;
      console.log("✅ Using allUsersData.data");
    } else if (Array.isArray(allUsersData)) {
      users = allUsersData;
      console.log("✅ Using allUsersData directly");
    } else {
      console.log("❌ Could not find users array in response");
      console.log("   allUsersData keys:", Object.keys(allUsersData || {}));
    }
    
    console.log("📋 All Users from API:", users);
    
    // Do not filter by role, include all users for name lookup
    return users;
  }, [allUsersData]);

  useEffect(() => {
    // Fetch all users for dropdown
    dispatch(getAllUserAction());
    dispatch(clearGetAllDailyPunchesAction());
    // On login, set selectedMonth to current month and fetch logs for logged-in user
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setSelectedMonth(currentMonth);
    setSelectedUserId(loggedInUserId);
    dispatch(getAllDailyPunchesAction({ endPoint: `employee-daily-travel?userId=${loggedInUserId}&month=${currentMonth}` }));
    setShowOtherUserData(false);
    // eslint-disable-next-line
  }, [dispatch, loggedInUserId]);

  // When selectedUserId or selectedMonth changes
  useEffect(() => {
    // Always clear activities when user/month changes
    dispatch(clearGetAllDailyPunchesAction());
    // If logged-in user is selected, always show their activities for selected month
    if (selectedUserId === loggedInUserId || !selectedUserId) {
      dispatch(getAllDailyPunchesAction({ endPoint: `employee-daily-travel?userId=${loggedInUserId}&month=${selectedMonth}` }));
      setShowOtherUserData(false);
    } else {
      // For other users, hide data until Get Activity is clicked
      setShowOtherUserData(false);
    }
    // eslint-disable-next-line
  }, [selectedUserId, selectedMonth, loggedInUserId, dispatch]);

  // Remove modal view logic

  const totalCount = successData?.data?.count ?? records.length;

  // Find an incomplete record belonging to the logged-in user
  // Returns { record, isPreviousDay } or null
  const incompleteData = useMemo(() => {
    if (isRestrictedRole && selectedUserId && selectedUserId !== loggedInUserId) {
      return null; // Don't show warning for other users
    }

    const isNonWorking = (record) => {
      const status = String(record?.dayStatus ?? record?.day_status ?? "").toUpperCase();
      return status === "LEAVE" || status === "HOLIDAY";
    };

    const incompleteRec = records.find(
      (r) =>
        r.userId === loggedInUserId &&
        (r.endPunchTime === null || r.endPunchTime === undefined) &&
        !isNonWorking(r)
    );
    
    if (!incompleteRec) return null;
    
    return {
      record: incompleteRec,
      isPreviousDay: isPreviousDayRecord(incompleteRec)
    };
  }, [records, loggedInUserId, isRestrictedRole, selectedUserId]);

  const handleEdit = (record) => {
    const id = record.travelId ?? record.id;
    dispatch(getDailyPunchByIdAction({ endPoint: `employee-daily-travel/${id}` }));
    navigate(punchingLogPath, { state: { editRecord: record } });
  };

  const handleView = (record) => {
    const id = record.travelId ?? record.id;
    navigate(`${punchingLogPath}/view/${id}`);
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    setShowOtherUserData(false);
    if (userId) {
      setSearchParams(params => ({ ...params, userId, month: selectedMonth }));
    } else {
      setSearchParams(params => ({ ...params, userId: "", month: selectedMonth }));
    }
  };

  const handleMonthChange = (e) => {
    const month = e.target.value;
    setSelectedMonth(month);
    setSearchParams(params => ({ ...params, userId: selectedUserId, month }));
  };

  // Excel export handler
  const handleExportExcel = () => {
    if (!records || records.length === 0) {
      alert("No data to export");
      return;
    }

    // Prepare data for export with clear columns
    const exportData = records.map((record) => {
      // Find user name from allUsers
      const user = allUsers.find(u => u.userId === record.userId);
      return {
        // "User Name": user ? (user.name || user.userName || `User ${user.userId}`) : record.userId ?? "",
        "Travel Date": record.travelDate ?? '',
        "Start Time": formatDateTime(record.startPunchTime),
        "Start Reading": record.odometerStart ?? '-',
        "End Time": formatDateTime(record.endPunchTime),
        "End Reading": record.odometerEnd ?? '-',
        "Total Distance (km)": getDistance(record).replace(" km", ""),
        
      };
    });

    // Add header row for clarity
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Travel Logs");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const filename = `TravelLogs_${selectedUserId || "All"}_${selectedMonth}.xlsx`;
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  return (
    <Box sx={{ p: 2, minHeight: "100vh", width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: { xs: 9, sm: 2 } }}>
      {/* Header */}
      <Typography variant="h6" fontWeight={700} sx={{ mt: {xs:0,sm:5,md:0}, mb: 2 }}>
        Travel Punching Logs
      </Typography>

      {/* Filter Bar for admin, AE, ZM, SO */}
      {(() => {
        // Show card for admin, AE, ZM, SO, MDO
        const allowedRoles = ["admin", "ae", "zm", "so", "mdo"];
        const normalizedRole = userRole ? userRole.toLowerCase().trim() : "";
        if (!allowedRoles.includes(normalizedRole)) return null;
        return (
         <Card>
  <Grid container spacing={2} mb={2} mt={1} ml={3} mr={2} alignItems="center">

    {/* Select User */}
    <Grid item size={{xs:6,sm:6,md:3}}>
      <FormControl fullWidth size="small">
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
          Select User
        </Typography>
        <Select
          value={selectedUserId}
          onChange={handleUserChange}
          displayEmpty
        >
          <MenuItem value="">All Users</MenuItem>
          {allUsers.length === 0 ? (
            <MenuItem disabled>No users found</MenuItem>
          ) : (
            allUsers.map((user) => (
              <MenuItem key={user.userId} value={user.userId}>
                {user.name
                  ? `${user.name} (${user.role || "Unknown Role"})`
                  : `User ${user.userId}`}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Grid>

    {/* Select Month */}
    <Grid item size={{xs:6,sm:6,md:3}}>
      <FormControl fullWidth size="small">
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
          Select Month
        </Typography>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
        >
          {Array.from({ length: 12 }).map((_, idx) => {
            const now = new Date();
            const m = new Date(now.getFullYear(), now.getMonth() - idx, 1);
            const value = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
            const label = m.toLocaleString("en-IN", { month: "long", year: "numeric" });

            return (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Grid>

    {/* Get Activity */}
    <Grid item size={{xs:6,sm:4,md:2}}>
      <Button
        variant="contained"
        fullWidth
        sx={{ borderRadius: 2, fontWeight: 600 ,mt:2.5}}
        disabled={!selectedUserId || !selectedMonth}
        onClick={() => {
          if (!selectedUserId || !selectedMonth) return;

          dispatch(clearGetAllDailyPunchesAction());

          const [year, month] = selectedMonth.split("-");
          const fromDate = `${year}-${month}-01`;

          const toDate = new Date(year, Number(month), 0);
          const toDateStr = `${year}-${month}-${String(toDate.getDate()).padStart(2, "0")}`;

          dispatch(
            getAllDailyPunchesAction({
              endPoint: `employee-daily-travel?user_id=${selectedUserId}&fromDate=${fromDate}&toDate=${toDateStr}`
            })
          );

          setShowOtherUserData(true);
        }}
      >
        Get Activity
      </Button>
    </Grid>

    {/* Export */}
    <Grid item size={{xs:6,sm:4,md:2}}>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        sx={{ borderRadius: 2, fontWeight: 600 ,mt:2.5}}
        disabled={records.length === 0}
        onClick={handleExportExcel}
      >
        Export
      </Button>
    </Grid>

    {/* Add Punching */}
    {(() => {
      const normalizedRole = userRole ? userRole.toLowerCase().trim() : "";

      if (["so", "mdo"].includes(normalizedRole)) {
        return (
          <Grid item size={{xs:12,sm:4,md:2}}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                backgroundColor: '#4F46E5',
                color: '#fff',
                '&:hover': { backgroundColor: '#3730A3' },
                mt: 2.5
              }}
              onClick={() => navigate(punchingLogPath)}
              disabled={!!(incompleteData && incompleteData.isPreviousDay)}
            >
              Add Punching
            </Button>
          </Grid>
        );
      }

      return null;
    })()}

  </Grid>
</Card>
        );
      })()}

      {/* Remove Add Punching button outside card for non-restricted roles. Button is now always inside the card. */}

      {/* Incomplete day ERROR - Only show if from PREVIOUS day, NOT today */}
      {!loading && incompleteData && !isRestrictedRole && incompleteData.isPreviousDay && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button
              color="error"
              size="small"
              variant="outlined"
              onClick={() => handleEdit(incompleteData.record)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Complete Now
            </Button>
          }
        >
          <AlertTitle>⚠️ Pending Day Closure</AlertTitle>
          You did not complete the end day details for {formatDateTime(incompleteData.record.startPunchTime).split(" ")[0]}. You must complete this before starting a new day.
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorInfo || "Failed to load travel punching logs."}
        </Alert>
      )}

      {/* Empty or missing filter for restricted roles */}
      {!loading && !error && records.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <DirectionsCarIcon sx={{ fontSize: 56, color: "#C7D2FE", mb: 1 }} />
          <Typography color="text.secondary" fontWeight={600}>
            {isRestrictedRole && (!selectedUserId || !selectedMonth)
              ? "Select user and month to view logs"
              : "No travel logs found"}
          </Typography>
        </Box>
      )}

      {/* List */}
      {!loading && records.length > 0 && (
        <Box>
          {/* ── Mobile cards (xs only) ── */}
          <Grid container spacing={2} sx={{ display: { xs: "flex", sm: "none" }, width: '100%', mt:2  }}>
            {records.map((record, idx) => {
              const isComplete = !!record.startPunchTime && !!record.endPunchTime;
              const isAdmin = userRole?.toLowerCase()?.trim() === "admin";
              const dayStatus = String(record.dayStatus ?? record.day_status ?? '').toUpperCase();
              let userName = "";
              let userRoleLabel = "";
              if (isAdmin) {
                const userObj = allUsers.find(u => u.userId === record.userId);
                userName = userObj && userObj.name ? userObj.name : "Unknown User";
                userRoleLabel = userObj && userObj.role ? userObj.role : "Unknown Role";
              }
              // If HOLIDAY or LEAVE, show only status and date, no actions
              if (dayStatus === "HOLIDAY" || dayStatus === "LEAVE") {
                return (
                  <Grid item size={{ xs: 12 }} key={record.travelId ?? idx} sx={{ maxWidth: '100% !important', width: '100%' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        p: 1.5,
                        border: "1px solid #E5E7EB",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                        overflowX: 'hidden',
                        wordBreak: 'break-word',
                        "&:hover, &:active": {
                          borderColor: "#2563EB",
                          boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                        },
                      }}
                    >
                      <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                        <Typography fontWeight={600} minWidth={120}>Date:</Typography>
                        <Typography variant="body2">{record.travelDate || '-'} </Typography>
                      </Box>
                      <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                        <Typography fontWeight={600} minWidth={120}>Status:</Typography>
                        <Typography variant="body2" color="error.main" fontWeight={600}>{dayStatus}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              }
              // Otherwise, show normal fields and actions
              return (
                <Grid item size={{ xs: 12 }} key={record.travelId ?? idx} sx={{ maxWidth: '100% !important', width: '100%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: 1.5,
                      border: "1px solid #E5E7EB",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      overflowX: 'hidden',
                      wordBreak: 'break-word',
                      "&:hover, &:active": {
                        borderColor: "#2563EB",
                        boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                      },
                    }}
                  >
                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                      <Typography fontWeight={600} minWidth={120}>Start Time:</Typography>
                      <Typography variant="body2">{formatDateTime(record.startPunchTime)}</Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                      <Typography fontWeight={600} minWidth={120}>Start Reading:</Typography>
                      <Typography variant="body2">{record.odometerStart ?? '-'} </Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                      <Typography fontWeight={600} minWidth={120}>End Time:</Typography>
                      <Typography variant="body2">{formatDateTime(record.endPunchTime)}</Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                      <Typography fontWeight={600} minWidth={120}>End Reading:</Typography>
                      <Typography variant="body2">{record.odometerEnd ?? '-'} </Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                      <Typography fontWeight={600} minWidth={120}>Total Distance:</Typography>
                      <Typography variant="body2">{getDistance(record)}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" mt={1}>
                      {isAdmin ? (
                        <IconButton size="small" aria-label="view" onClick={() => handleView(record)}>
                          <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                        </IconButton>
                      ) : (
                        isComplete ? (
                          <IconButton size="small" aria-label="view" onClick={() => handleView(record)}>
                            <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleEdit(record)}>
                            <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }} />
                          </IconButton>
                        )
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* ── Desktop rows (sm+) ── */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", gap: 1 }}>
            {/* Header row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: DESKTOP_COLS,
                px: 1.5,
                py: 1,
                gap: 1,
              }}
            >
              {HEADERS.map((h) => (
                <Typography
                  key={h}
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    textAlign: h === "Actions" ? "right" : "left",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: h === "Actions" ? "flex-end" : "flex-start",
                    minHeight: 32
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Value rows */}
            {records.map((record, idx) => {
              const isComplete = !!record.startPunchTime && !!record.endPunchTime;
              const isAdmin = userRole?.toLowerCase()?.trim() === "admin";
              const dayStatus = String(record.dayStatus ?? record.day_status ?? '').toUpperCase();
              let userName = "";
              let userRoleLabel = "";
              if (isAdmin) {
                const userObj = allUsers.find(u => u.userId === record.userId);
                userName = userObj && userObj.name ? userObj.name : "Unknown User";
                userRoleLabel = userObj && userObj.role ? userObj.role : "Unknown Role";
              }
              // If HOLIDAY or LEAVE, show only status and date, no actions
              if (dayStatus === "HOLIDAY" || dayStatus === "LEAVE") {
                return (
                  <Paper
                    key={record.travelId ?? idx}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      display: "grid",
                      gridTemplateColumns: DESKTOP_COLS,
                      alignItems: "center",
                      border: "1px solid #E5E7EB",
                      px: 2,
                      py: 1.5,
                      gap: 1,
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: "#2563EB",
                        boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                      },
                    }}
                  >
                    <Typography variant="body2">{record.travelDate || '-'}</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={600}>{dayStatus}</Typography>
                    <Typography variant="body2">-</Typography>
                    <Typography variant="body2">-</Typography>
                    <Typography variant="body2">-</Typography>
                    <Box />
                  </Paper>
                );
              }
              // Otherwise, show normal fields and actions
              return (
                <Paper
                  key={record.travelId ?? idx}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    display: "grid",
                    gridTemplateColumns: DESKTOP_COLS,
                    alignItems: "center",
                    border: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.5,
                    gap: 1,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: "#2563EB",
                      boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                    },
                  }}
                >
                  <Typography variant="body2">{formatDateTime(record.startPunchTime)}</Typography>
                  <Typography variant="body2">{record.odometerStart ?? '-'}</Typography>
                  <Typography variant="body2">{formatDateTime(record.endPunchTime)}</Typography>
                  <Typography variant="body2">{record.odometerEnd ?? '-'}</Typography>
                  <Typography variant="body2">{getDistance(record)}</Typography>
                  {/* {isAdmin && <Typography variant="body2">{userName} ({userRoleLabel})</Typography>} */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ height: '100%' }}
                  >
                      {isAdmin ? (
                        <IconButton size="small" aria-label="view" onClick={() => handleView(record)}>
                          <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                        </IconButton>
                      ) : (
                        isComplete ? (
                          <IconButton size="small" aria-label="view" onClick={() => handleView(record)}>
                            <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleEdit(record)}>
                            <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }} />
                          </IconButton>
                        )
                      )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* TravelPunchingLogView now handled by route, not modal */}
    </Box>
  );
};

export default TravelPunchingLogList;
